import React, {
  useEffect,
  useState,
  useCallback
} from "react";

import {
  TextField,
  Typography,
  Box,
  IconButton,
  Collapse
} from "@mui/material";

import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

import {
  useForm,
  Controller
} from "react-hook-form";

import { useEmployees } from "../../context/EmployeesContext";
import AppSelect from "../../ui/AppSelect";
import RichTextEditor from "../../ui/RichTextEditor";

import {
  getCategories
} from "../../services/categoryService";

import {
  getPriorities
} from "../../services/priorityService";

import {
  getProjects
} from "../../services/projectService";

import {
  getComments,
  createComment
} from "../../services/commentService";

import { useUserRole } from "../../context/UserRoleContext";
import CommentThread from "../common/CommentThread";

const TICKET_TYPES = ["Task", "Bug", "Story"];
const TICKET_TYPE_OPTIONS = TICKET_TYPES.map((type) => ({ value: type, label: type }));

function TicketForm({
  defaultValues,
  onSubmit,
  submitRef,
  onValidityChange
}) {
  const {
    register,
    handleSubmit,
    reset,
    control,
    trigger,
    watch,
    formState: { errors, isValid, touchedFields, isSubmitted }
  } = useForm({ mode: "onChange" });

  const { employees } = useEmployees();
  const [categories, setCategories] = useState([]);
  const [priorities, setPriorities] = useState([]);
  const [projects, setProjects] = useState([]);
  const [detailsOpen, setDetailsOpen] = useState(true);
  const [moreFieldsOpen, setMoreFieldsOpen] = useState(true);

  const { employee } = useUserRole();

  const loadMasterData = useCallback(async () => {
    // Load each list independently so one failing endpoint doesn't leave
    // the others stuck on "Loading..." forever.
    const [categoryResult, priorityResult, projectResult] = await Promise.allSettled([
      getCategories(),
      getPriorities(),
      getProjects()
    ]);

    if (categoryResult.status === "fulfilled") {
      setCategories(categoryResult.value);
    } else {
      console.error("Category Master Data Error:", categoryResult.reason);
    }

    if (priorityResult.status === "fulfilled") {
      setPriorities(priorityResult.value);
    } else {
      console.error("Priority Master Data Error:", priorityResult.reason);
    }

    if (projectResult.status === "fulfilled") {
      setProjects(projectResult.value);
    } else {
      console.error("Project Master Data Error:", projectResult.reason);
    }
  }, []);

  useEffect(() => {
    loadMasterData();

    if (defaultValues) {
      reset(defaultValues);
    }

    trigger();
  }, [defaultValues, reset, loadMasterData, trigger]);

  useEffect(() => {
    onValidityChange?.(isValid);
  }, [isValid, onValidityChange]);

  const watchedValues = watch();

  useEffect(() => {
    console.log("[TicketForm] isValid:", isValid);
    console.log("[TicketForm] errors:", errors);
    console.log("[TicketForm] values:", watchedValues);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isValid, errors, JSON.stringify(watchedValues)]);

  const handleFormSubmit = useCallback((data) => {
    const payload = {
      title: data.title,
      ticket_type: data.ticket_type || "Task",
      description: data.description,
      category_id: Number(data.category_id),
      priority_id: Number(data.priority_id),
      // New tickets always start Open; edits preserve the current status
      status_id: defaultValues?.status_id || 5,
      assigned_to: data.assigned_to ? Number(data.assigned_to) : null,
      project_id: data.project_id ? Number(data.project_id) : null,
      created_by: data.reporter_id ? Number(data.reporter_id) : null
    };

    onSubmit(payload);
  }, [onSubmit, defaultValues]);

  useEffect(() => {
    if (submitRef) {
      submitRef.current = () => {
        handleSubmit(handleFormSubmit)();
      };
    }
  }, [submitRef, handleSubmit, handleFormSubmit]);

  return (
    <form style={{ height: "100%" }}>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "minmax(0, 4fr) minmax(0, 1fr)" },
          gap: 3,
          height: "100%"
        }}
      >
        {/* Left column: Title + Description */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2.2 }}>
          <Box>
            <Typography sx={{ fontSize: 13, fontWeight: 700, color: "text.primary", mb: 0.8 }}>
              Title <Box component="span" sx={{ color: "#d32f2f" }}>*</Box>
            </Typography>

            <TextField
              fullWidth
              size="small"
              placeholder="Enter ticket title"
              error={Boolean(errors.title) && (touchedFields.title || isSubmitted)}
              helperText={errors.title && (touchedFields.title || isSubmitted) ? "Title is required" : ""}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                  bgcolor: "background.paper"
                },
                "& .MuiFormHelperText-root": {
                  minHeight: 20,
                  mt: 0.7
                }
              }}
              {...register("title", { required: true })}
            />
          </Box>

          <Box sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
            <Typography sx={{ fontSize: 13, fontWeight: 700, color: "text.primary", mb: 0.8 }}>
              Description <Box component="span" sx={{ color: "#d32f2f" }}>*</Box>
            </Typography>

            <Controller
              name="description"
              control={control}
              defaultValue={defaultValues?.description || ""}
              rules={{ required: true }}
              render={({ field }) => (
                <RichTextEditor
                  className="ticket-description-editor"
                  value={field.value}
                  onChange={field.onChange}
                  placeholder="Enter your Description (min 100 words)"
                  enableImagePaste
                />
              )}
            />

            {errors.description && (touchedFields.description || isSubmitted) && (
              <Typography sx={{ fontSize: 12, color: "#d32f2f", mt: 0.7, ml: 1.7 }}>
                Description is required
              </Typography>
            )}
          </Box>

          {defaultValues?.ticket_id ? (
            <CommentThread
              key={`ticket-${defaultValues.ticket_id}`}
              entityId={defaultValues.ticket_id}
              members={employees}
              currentEmployeeId={employee?.employee_id}
              currentEmployeeName={employee?.employee_name}
              fetchComments={getComments}
              postComment={createComment}
            />
          ) : (
            <Box>
              <Typography sx={{ fontSize: 13, fontWeight: 700, color: "text.primary", mb: 0.8 }}>
                Activity
              </Typography>
              <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
                Comments will be available once this ticket is created.
              </Typography>
            </Box>
          )}
        </Box>

        {/* Right column: Details (Type, Category, Priority, Assign To, Reporter) + More Fields (Project) */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2.2 }}>
          <Box sx={{ bgcolor: "background.paper", border: "1px solid", borderColor: "divider", borderRadius: 2, overflow: "hidden" }}>
            <Box
              onClick={() => setDetailsOpen((prev) => !prev)}
              sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 2, py: 1.4, cursor: "pointer" }}
            >
              <Typography sx={{ fontSize: 11, fontWeight: 800, color: "text.secondary", textTransform: "uppercase", letterSpacing: 0.5 }}>
                Details
              </Typography>
              <IconButton size="small">
                <ExpandMoreIcon
                  fontSize="small"
                  sx={{ transform: detailsOpen ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.15s ease" }}
                />
              </IconButton>
            </Box>

            <Collapse in={detailsOpen}>
              <Box sx={{ px: 2, pb: 2, display: "flex", flexDirection: "column", gap: 2 }}>
                <Box>
                  <Typography sx={{ fontSize: 13, fontWeight: 700, color: "text.primary", mb: 0.8 }}>
                    Type <Box component="span" sx={{ color: "#d32f2f" }}>*</Box>
                  </Typography>

                  <Controller
                    name="ticket_type"
                    control={control}
                    defaultValue={defaultValues?.ticket_type || "Task"}
                    rules={{ required: true }}
                    render={({ field }) => (
                      <AppSelect
                        options={TICKET_TYPE_OPTIONS}
                        value={field.value ?? "Task"}
                        onChange={field.onChange}
                      />
                    )}
                  />
                </Box>

                <Box>
                  <Typography sx={{ fontSize: 13, fontWeight: 700, color: "text.primary", mb: 0.8 }}>
                    Category <Box component="span" sx={{ color: "#d32f2f" }}>*</Box>
                  </Typography>

                  <Controller
                    name="category_id"
                    control={control}
                    defaultValue={defaultValues?.category_id || ""}
                    rules={{ required: true }}
                    render={({ field }) => (
                      <AppSelect
                        options={categories.map((item) => ({ value: item.category_id, label: item.category_name }))}
                        value={field.value ?? ""}
                        onChange={field.onChange}
                        isDisabled={categories.length === 0}
                        isLoading={categories.length === 0}
                        placeholder={categories.length === 0 ? "Loading categories..." : "Select category"}
                        error={Boolean(errors.category_id) && (touchedFields.category_id || isSubmitted)}
                        helperText={errors.category_id && (touchedFields.category_id || isSubmitted) ? "Please select a category" : ""}
                      />
                    )}
                  />
                </Box>

                <Box>
                  <Typography sx={{ fontSize: 13, fontWeight: 700, color: "text.primary", mb: 0.8 }}>
                    Priority <Box component="span" sx={{ color: "#d32f2f" }}>*</Box>
                  </Typography>

                  <Controller
                    name="priority_id"
                    control={control}
                    defaultValue={defaultValues?.priority_id || ""}
                    rules={{ required: true }}
                    render={({ field }) => (
                      <AppSelect
                        options={priorities.map((item) => ({ value: item.priority_id, label: item.priority_name }))}
                        value={field.value ?? ""}
                        onChange={field.onChange}
                        isDisabled={priorities.length === 0}
                        isLoading={priorities.length === 0}
                        placeholder={priorities.length === 0 ? "Loading priorities..." : "Select priority"}
                        error={Boolean(errors.priority_id) && (touchedFields.priority_id || isSubmitted)}
                        helperText={errors.priority_id && (touchedFields.priority_id || isSubmitted) ? "Please select a priority" : ""}
                      />
                    )}
                  />
                </Box>

                <Box>
                  <Typography sx={{ fontSize: 13, fontWeight: 700, color: "text.primary", mb: 0.8 }}>
                    Assign To <Box component="span" sx={{ color: "#d32f2f" }}>*</Box>
                  </Typography>

                  <Controller
                    name="assigned_to"
                    control={control}
                    defaultValue={defaultValues?.assigned_to || ""}
                    rules={{ required: true }}
                    render={({ field }) => (
                      <AppSelect
                        options={employees.map((employee) => ({ value: employee.employee_id, label: employee.employee_name }))}
                        value={field.value ?? ""}
                        onChange={field.onChange}
                        isDisabled={employees.length === 0}
                        isLoading={employees.length === 0}
                        placeholder={employees.length === 0 ? "Loading employees..." : "Select employee"}
                        error={Boolean(errors.assigned_to) && (touchedFields.assigned_to || isSubmitted)}
                        helperText={errors.assigned_to && (touchedFields.assigned_to || isSubmitted) ? "Please select an employee" : ""}
                        withAvatar
                      />
                    )}
                  />
                </Box>

                <Box>
                  <Typography sx={{ fontSize: 13, fontWeight: 700, color: "text.primary", mb: 0.8 }}>
                    Report To <Box component="span" sx={{ color: "#d32f2f" }}>*</Box>
                  </Typography>

                  <Controller
                    name="reporter_id"
                    control={control}
                    defaultValue={defaultValues?.created_by_id || employee?.employee_id || ""}
                    rules={{ required: true }}
                    render={({ field }) => (
                      <AppSelect
                        options={employees.map((reporter) => ({ value: reporter.employee_id, label: reporter.employee_name }))}
                        value={field.value ?? ""}
                        onChange={field.onChange}
                        isDisabled={employees.length === 0}
                        isLoading={employees.length === 0}
                        placeholder={employees.length === 0 ? "Loading employees..." : "Select reporter"}
                        error={Boolean(errors.reporter_id) && (touchedFields.reporter_id || isSubmitted)}
                        helperText={errors.reporter_id && (touchedFields.reporter_id || isSubmitted) ? "Please select a reporter" : ""}
                        withAvatar
                      />
                    )}
                  />
                </Box>
              </Box>
            </Collapse>
          </Box>

          <Box sx={{ bgcolor: "background.paper", border: "1px solid", borderColor: "divider", borderRadius: 2, overflow: "hidden" }}>
            <Box
              onClick={() => setMoreFieldsOpen((prev) => !prev)}
              sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 2, py: 1.4, cursor: "pointer" }}
            >
              <Typography sx={{ fontSize: 11, fontWeight: 800, color: "text.secondary", textTransform: "uppercase", letterSpacing: 0.5 }}>
                More Fields
              </Typography>
              <IconButton size="small">
                <ExpandMoreIcon
                  fontSize="small"
                  sx={{ transform: moreFieldsOpen ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.15s ease" }}
                />
              </IconButton>
            </Box>

            <Collapse in={moreFieldsOpen}>
              <Box sx={{ px: 2, pb: 2, display: "flex", flexDirection: "column", gap: 2 }}>
                <Box>
                  <Typography sx={{ fontSize: 13, fontWeight: 700, color: "text.primary", mb: 0.8 }}>
                    Project <Box component="span" sx={{ color: "#d32f2f" }}>*</Box>
                  </Typography>

                  <Controller
                    name="project_id"
                    control={control}
                    defaultValue={defaultValues?.project_id || ""}
                    rules={{ required: true }}
                    render={({ field }) => (
                      <AppSelect
                        options={projects.map((project) => ({ value: project.project_id, label: project.project_name }))}
                        value={field.value ?? ""}
                        onChange={field.onChange}
                        isDisabled={projects.length === 0 || Boolean(defaultValues?.ticket_id)}
                        isLoading={projects.length === 0}
                        placeholder={projects.length === 0 ? "Loading projects..." : "Select project"}
                        error={Boolean(errors.project_id) && (touchedFields.project_id || isSubmitted)}
                        helperText={
                          errors.project_id && (touchedFields.project_id || isSubmitted)
                            ? "Please select a project"
                            : defaultValues?.ticket_id
                              ? "Project cannot be changed after a ticket is created"
                              : ""
                        }
                      />
                    )}
                  />
                </Box>
              </Box>
            </Collapse>
          </Box>
        </Box>
      </Box>
    </form>
  );
}

export default TicketForm;
